import {
  Row,
  Col,
  Card,
  Radio,
  Button,
  Typography,
  Modal,
  Form,
  Input,
  Upload,
  message,
} from "antd";
import { useEffect, useState } from "react";
import { InboxOutlined, PlusOutlined } from "@ant-design/icons";
import {
  addFirmwareAPI,
  getFirmwareAPI,
  publishV2FirmwareAPI,
} from "../api/firmware";
import {
  buildFirmwareFormData,
  isFirmwareUploadSuccess,
  getFirmwareUploadError,
  parseFirmwareOffers,
  V2_CDN_OFFER,
} from "./firmwareUpload";

const { Title, Text } = Typography;

function OfferTable({ offer, empty }) {
  if (!offer) {
    return (
      <div className="ant-list-box table-responsive m-25">
        <Text type="secondary">{empty}</Text>
      </div>
    );
  }
  return (
    <div className="ant-list-box table-responsive m-25">
      <table className="width-100">
        <thead>
          <tr>
            <th>
              <div className="text-center">Firmware Version</div>
            </th>
            <th>
              <div className="text-center">Who sees this</div>
            </th>
            <th>
              <div className="text-center">Firmware URL</div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <div className="text-xs text-center">
                <Title level={5}>{offer.version}</Title>
              </div>
            </td>
            <td>
              <div className="text-xs text-center">{offer.audience}</div>
            </td>
            <td>
              <div className="text-xs text-center">
                <a href={offer.url} target="_blank" rel="noopener noreferrer">
                  {offer.url}
                </a>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function Firmware() {
  const [form] = Form.useForm();
  const [offers, setOffers] = useState({ v1: null, v2: null });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [publishingV2, setPublishingV2] = useState(false);

  const _addFirmware = async (values) => {
    try {
      const { formData, error } = buildFirmwareFormData(values);

      if (error) {
        message.error(error);
        return;
      }

      const res = await addFirmwareAPI(formData);

      if (isFirmwareUploadSuccess(res)) {
        message.success("Firmware uploaded successfully");
        _getFirmware();
        setIsModalOpen(false);
        form.resetFields();
      } else {
        message.error(getFirmwareUploadError(res));
      }
    } catch (e) {
      console.log("Error", e);
      message.error("Failed to upload firmware");
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };
  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const _getFirmware = async () => {
    try {
      const res = await getFirmwareAPI();
      const parsed = parseFirmwareOffers(res);
      if (parsed) {
        setOffers(parsed);
      }
    } catch (e) {
      console.log("Error", e);
    }
  };

  const publishV2FromCdn = async () => {
    setPublishingV2(true);
    try {
      const res = await publishV2FirmwareAPI(V2_CDN_OFFER);
      if (isFirmwareUploadSuccess(res)) {
        message.success("V2 app offer is 1.5.2. V1 fleet is still 1.4.1.");
        _getFirmware();
      } else {
        message.error(getFirmwareUploadError(res));
      }
    } catch (e) {
      console.log("Error", e);
      message.error("Failed to publish the V2 offer");
    } finally {
      setPublishingV2(false);
    }
  };

  const onChange = (e) => console.log(`radio checked:${e.target.value}`);

  const uploadProps = {
    name: "firmware",
    multiple: false,
    maxCount: 1,
    beforeUpload: () => {
      return false;
    },
    onChange(info) {
      if (info.fileList.length > 0) {
        const file = info.fileList[0];
        if (file.size > 1024 * 1024 * 10) {
          message.error("File must be smaller than 10MB!");
        }
      }
    },
  };

  useEffect(() => {
    _getFirmware();
  }, []);

  return (
    <>
      <div className="tabled">
        <Row gutter={[24, 0]}>
          <Col xs="24" xl={24}>
            <Card
              bordered={false}
              className="criclebox tablespace mb-24"
              title="V1 app — fleet"
              extra={
                <Radio.Group onChange={onChange} defaultValue="a">
                  <Radio.Button value="a" onClick={showModal}>
                    <PlusOutlined />
                    <span style={{ marginLeft: 10 }}>
                      Upload New Firmware
                    </span>
                  </Radio.Button>
                </Radio.Group>
              }
            >
              <OfferTable
                offer={
                  offers.v1
                    ? {
                        ...offers.v1,
                        audience: "Old app. Every cooker that is not updating.",
                      }
                    : null
                }
                empty="No v1 fleet pointer."
              />
            </Card>
          </Col>
          <Col xs="24" xl={24}>
            <Card
              bordered={false}
              className="criclebox tablespace mb-24"
              title="V2 app only"
              extra={
                !offers.v2 ? (
                  <Button
                    type="primary"
                    loading={publishingV2}
                    onClick={publishV2FromCdn}
                  >
                    Use the 1.5.2 file already on CDN
                  </Button>
                ) : null
              }
            >
              <OfferTable
                offer={
                  offers.v2
                    ? {
                        ...offers.v2,
                        audience:
                          "V2 app only. A cooker gets this URL after that user requests an update.",
                      }
                    : null
                }
                empty="The 1.5.2 file is on Spaces. This offer is not published yet — v1 users cannot see it."
              />
            </Card>
          </Col>
        </Row>
      </div>
      <Modal
        title="Add Firmware"
        open={isModalOpen}
        okCancel={false}
        onCancel={handleCancel}
        footer={null}
      >
        <Form
          name="basic"
          labelCol={{
            span: 6,
          }}
          wrapperCol={{
            span: 18,
          }}
          style={{
            maxWidth: 600,
          }}
          onFinish={_addFirmware}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
          form={form}
        >
          <Form.Item
            label="Firmware Version"
            name="firmware_version"
            rules={[
              {
                required: true,
                message: "Please enter firmware version!",
              },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Firmware File"
            name="firmware"
            rules={[
              {
                required: true,
                message: "Please upload firmware file!",
              },
            ]}
          >
            <Upload.Dragger {...uploadProps}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                Click or drag firmware file to this area to upload
              </p>
              <p className="ant-upload-hint">
                1.4.1 updates the v1 fleet. 1.5.2 / 1.6.2+ update the V2 offer
                only.
              </p>
            </Upload.Dragger>
          </Form.Item>

          <Form.Item
            wrapperCol={{
              offset: 20,
              span: 4,
            }}
          >
            <Button type="primary" htmlType="submit">
              Submit
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export default Firmware;
